/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { AlertColor } from '@mui/material';
import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { uiActions } from '@opengeoweb/store';
import type { DrawingListItem } from '../warningsDrawings/types';
import { withDrawingFeatureIds } from '../warningsDrawings/utils';
import type { WarningEvent } from '../warningsForm/types';
import { warningEventDialogType } from '../warningsForm/utils';

export type FormAction =
  | ''
  | 'saving'
  | 'publishing'
  | 'readonly'
  | 'edit'
  | 'amending'
  | 'cancel';

export interface FormError {
  severity?: AlertColor;
  message: string;
}

export interface WarningsEventFormState {
  // form data
  event?: WarningEvent;
  // form state
  formState?: FormAction;
  error?: FormError;
  selectedEventId?: string;
  isFormDirty?: boolean;
  warningType?: 'warningsEvent';
  panelId?: string;
  selectedDrawing?: DrawingListItem;
  hoveredDrawing?: DrawingListItem;
}
export const TAKEOVER_MESSAGE = 'You are no longer the editor of this warning';

export const initialState: WarningsEventFormState = {};

const slice = createSlice({
  initialState,
  name: 'warningsEventForm',
  reducers: {
    setFormValues: (
      draft: Draft<WarningsEventFormState>,
      action: PayloadAction<{
        event?: WarningEvent;
        formState?: FormAction;
      }>,
    ) => {
      const { event, formState } = action.payload;
      if (event) {
        draft.event = event;
        draft.selectedDrawing =
          event.selectedDrawing && withDrawingFeatureIds(event.selectedDrawing);
      }

      if (formState !== undefined) {
        draft.formState = formState;
      }
    },
    openWarningsEventFormDialog: (
      draft: Draft<WarningsEventFormState>,
      action: PayloadAction<{
        formState?: FormAction;
        event?: WarningEvent;
        panelId?: string;
        selectedEventId?: string;
      }>,
    ) => {
      const {
        event,
        formState = '',
        panelId,
        selectedEventId,
      } = action.payload;
      draft.event = event;
      draft.selectedDrawing =
        event?.selectedDrawing && withDrawingFeatureIds(event.selectedDrawing);
      draft.selectedEventId = selectedEventId || event?.eventId;
      draft.formState = formState;
      draft.panelId = panelId;
    },
    setFormError: (
      draft: Draft<WarningsEventFormState>,
      action: PayloadAction<FormError>,
    ) => {
      const { message, severity = 'error' } = action.payload;
      if (draft.error?.severity === 'error' && severity === 'warning') {
        return; // Do not overwrite error with warning
      }
      if (severity !== 'warning') {
        draft.formState = '';
      }
      draft.error = {
        severity,
        message,
      };
    },
    removeFormError: (draft: Draft<WarningsEventFormState>) => {
      delete draft.error;
    },
    setFormDirty: (
      draft: Draft<WarningsEventFormState>,
      action: PayloadAction<{
        isFormDirty: boolean;
      }>,
    ) => {
      const { isFormDirty } = action.payload;
      draft.isFormDirty = isFormDirty;
    },
    setSelectedEventId: (
      draft: Draft<WarningsEventFormState>,
      action: PayloadAction<string>,
    ) => {
      draft.selectedEventId = action.payload;
    },
    setSelectedDrawing: (
      draft: Draft<WarningsEventFormState>,
      action: PayloadAction<{ drawing?: DrawingListItem }>,
    ) => {
      const { drawing } = action.payload;
      draft.selectedDrawing = drawing && withDrawingFeatureIds(drawing);
    },
    setHoveredDrawing: (
      draft: Draft<WarningsEventFormState>,
      action: PayloadAction<{ drawing?: DrawingListItem }>,
    ) => {
      const { drawing } = action.payload;
      if (!drawing) {
        delete draft.hoveredDrawing;
        return;
      }
      draft.hoveredDrawing = withDrawingFeatureIds(drawing);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      uiActions.setToggleOpenDialog,
      (draft: Draft<WarningsEventFormState>, action) => {
        const { type, setOpen } = action.payload;
        if (type === warningEventDialogType && !setOpen) {
          // delete draft.object;
          delete draft.event;
          delete draft.formState;
          delete draft.error;
          delete draft.selectedEventId;
          delete draft.isFormDirty;
          delete draft.warningType;
          delete draft.selectedDrawing;
          delete draft.hoveredDrawing;
        }
      },
    );
  },
});

export const {
  reducer: warningsEventFormReducer,
  actions: warningsEventFormActions,
} = slice;
